import localforage from "localforage";
import db, { NoEncryptionKeyError } from "./database/database";
import { errorLog, warnLog } from "./utils/logger";

/** Unencrypted preferences live in their own IndexedDB database, readable before unlock. */
const unencryptedPrefs = localforage.createInstance({ name: "UnencryptedPrefs" });

export interface Persisted<T> {
  /** The current value. It is `init` until the stored value has loaded; assigning writes through. */
  current: T;
  /**
   * Store `next`, then make it current: for a change that must outlive the page, which can go any
   * moment after the change shows (a reload, say). Settles once it's current. A failed write is
   * logged, and the value changes anyway; a later assignment wins over it.
   */
  set(next: T): Promise<void>;
  /** Settles once the stored value has been read (or the read failed and `init` was kept). */
  readonly ready: Promise<void>;
  /** Whether the last read of the stored value failed, so `current` may not be what's stored. */
  readonly failed: boolean;
  /** Read the stored value again, e.g. once the encrypted database is unlocked. */
  reload(): Promise<void>;
  /** Forget the stored value and go back to `init`. */
  reset(): Promise<void>;
}

/**
 * A reactive preference kept in storage under `key`, with the same keys and stores as the React app:
 * the "UnencryptedPrefs" localforage database, or the encrypted db() for `{ encrypted: true }`.
 *
 * A stored null means "unset" and reads as `init`, as it did before. Encrypted values can't be read
 * while the database is locked; they stay at `init` until `reload()` runs after unlock.
 */
export function persisted<T>(
  key: string,
  init: T,
  { encrypted = false }: { encrypted?: boolean } = {},
): Persisted<T> {
  let value = $state(init);
  // Bumped by every assignment, so a read that finishes after one doesn't overwrite it.
  let version = 0;
  let failed = false;

  const read = async () => {
    const readVersion = version;
    try {
      const stored = encrypted
        ? await db().getItem<T>(key)
        : await unencryptedPrefs.getItem<T>(key);
      // When IndexedDB won't open, localforage moves to an empty store of another kind, whose answer
      // isn't what's stored.
      failed = !encrypted && unencryptedPrefs.driver() !== localforage.INDEXEDDB;
      if (readVersion === version) {
        value = stored ?? init;
      }
    } catch (err) {
      failed = true;
      if (err instanceof NoEncryptionKeyError) {
        warnLog(err.message);
      } else {
        errorLog(err, `Could not read preference '${key}'.`);
      }
    }
  };

  const write = async (next: T) => {
    try {
      if (encrypted) {
        await db().setItem(key, next);
      } else {
        await unencryptedPrefs.setItem(key, next);
      }
    } catch (err) {
      errorLog(err, `Could not save preference '${key}'.`);
    }
  };

  const set = async (next: T) => {
    const setVersion = ++version;
    await write(next);
    if (setVersion === version) {
      value = next;
    }
  };

  let ready = read();

  return {
    get current() {
      return value;
    },
    set current(next: T) {
      version++;
      value = next;
      void write(next);
    },
    set,
    get ready() {
      return ready;
    },
    get failed() {
      return failed;
    },
    reload() {
      ready = read();
      return ready;
    },
    async reset() {
      version++;
      value = init;
      await (encrypted ? db().removeItem(key) : unencryptedPrefs.removeItem(key));
    },
  };
}

/** Dark mode (the "docket-dark" theme). Not offered on the NJ domains. */
export const darkMode = persisted("darkMode", false);

/** Whether `email` accepted the consent form, on this device (the React app's key). */
export const consentAccepted = (email: string) => persisted(`consentAccepted_${email}`, false);

/** Ask for biometrics when the app opens (native only). Forgotten at logout, as before. */
export const biometricAuth = persisted("biometricAuth", false);

/** Whether the app has asked for a rating (the React app's key). It stays after logout, as before. */
export const appRated = persisted("appRated", false);

/**
 * The React app kept the signed-in email in "appSettings", unencrypted. Nothing reads it any more,
 * but logout still blanks it, as before, for anyone who used that app. Its other fields stay.
 */
export async function forgetSignIn(): Promise<void> {
  const settings = await unencryptedPrefs.getItem<Record<string, unknown>>("appSettings");
  await unencryptedPrefs.setItem("appSettings", {
    firebaseEmulatorConnected: false,
    ...settings,
    email: "",
    authorized: false,
    signedInWithFirebase: false,
  });
}
